import { CaretDownOutlined } from '@vindr/icons';
import { ParamsGetOrderWorklist } from '@vindr/services/apis/order.api';
import { ORDER_STATUS } from '@vindr/services/constants/order.constant';
import { MATCH_STATUS } from '@vindr/services/constants/study.constant';
import { DateRangePicker, Filter } from '@vindr/ui';
import { Omit, enumKeys, filterBothValueAndKey } from '@vindr/utils/helpers/common.helper';
import { Input, Select } from 'antd';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FILTER_TYPE } from './constants';
import { MODALITIES } from '@vindr/services/constants/study.constant';
import { ESIGN_STATUS } from '@vindr/services/apis/esign.api';
import { INDICATOR } from '@vindr/services/apis/report.api';

const { Option } = Select;

export enum WorklistFilters {
  STATUS,
  PATIENT_ID,
  PATIENT_NAME,
  MODALITY,
  RECEIVED_AT,
  INSTITUTION,
  STUDY_DESCRIPTION,
  VISIT_ID,
  ACCESSION_NUMBER,
  MATCH_STATUS,
  STUDY_ID,
  ORDER_DATE,
}

const useSearchOptions = (params: ParamsGetOrderWorklist, handleOnChangeFilter) => {
  const { t } = useTranslation('Vindoc');
  const [open, setOpen] = useState(false);

  const InputItem = (params, key, placeholder = t('Keyword')) => {
    return (
      <Input
        allowClear
        className="w-[200px]"
        placeholder={placeholder}
        size="small"
        value={params[key]}
        onChange={(event) => handleOnChangeFilter(params, key, event?.target?.value)}
        onPressEnter={(event) => {
          return handleOnChangeFilter(params, key, (event.target as HTMLInputElement).value, true);
        }}
      />
    );
  };

  return useMemo(
    () => [
      {
        text: t('Status'),
        key: FILTER_TYPE.STATUS,
        component: (
          <Select
            allowClear
            className="select-dropdown-light w-[200px]"
            maxTagCount="responsive"
            mode="multiple"
            open={open}
            placeholder={t('Select Status')}
            popupClassName="dropdown-options-dark"
            size="small"
            suffixIcon={<CaretDownOutlined />}
            value={params[FILTER_TYPE.STATUS] || []}
            onDropdownVisibleChange={(visible) => setOpen(visible)}
            onChange={(value) => {
              if (window.innerWidth < 640) {
                setOpen(false);
              }
              handleOnChangeFilter(params, FILTER_TYPE.STATUS, value, true);
            }}
          >
            {Object.keys(ORDER_STATUS)
              .filter((key) => isNaN(Number(key)))
              .map((status) => (
                <Select.Option key={status} value={ORDER_STATUS[status]}>
                  {t(status)}
                </Select.Option>
              ))}
          </Select>
        ),
      },
      {
        text: t('Patient ID'),
        key: FILTER_TYPE.PATIENT_ID,
        component: InputItem(params, FILTER_TYPE.PATIENT_ID, t('Enter Patient ID')),
      },
      {
        text: t('Patient Name'),
        key: FILTER_TYPE.PATIENT_NAME,
        component: InputItem(params, FILTER_TYPE.PATIENT_NAME, t('Enter Patient Name')),
      },
      {
        text: t('Modality'),
        key: FILTER_TYPE.MODALITY,
        component: (
          <Select
            allowClear
            showSearch
            className="select-dropdown-light w-[200px]"
            placeholder={t('Select Modality')}
            popupClassName="dropdown-options-dark"
            size="small"
            suffixIcon={<CaretDownOutlined />}
            value={params[FILTER_TYPE.MODALITY] || null}
            onChange={(value) => {
              handleOnChangeFilter(params, FILTER_TYPE.MODALITY, value, true);
            }}
          >
            {MODALITIES.map((it) => (
              <Option key={it} value={it}>
                {it}
              </Option>
            ))}
          </Select>
        ),
      },
      {
        text: t('Received At'),
        key: FILTER_TYPE.RECEIVED_AT,
        component: (
          <DateRangePicker
            allowClear
            disabledDate={() => false}
            size="small"
            value={[
              dayjs(params.patient_arrived_time_from || new Date()),
              dayjs(params.patient_arrived_time_to || new Date()),
            ]}
            onChange={(values: dayjs.Dayjs[]) => {
              handleOnChangeFilter(
                params,
                FILTER_TYPE.RECEIVED_AT,
                [values[0].startOf('days'), values[1].endOf('days')],
                true
              );
            }}
          />
        ),
      },
      {
        text: t('Institution'),
        key: FILTER_TYPE.INSTITUTION_ID,
        component: (
          <Filter.Institution
            allowClear
            multiple
            className="select-dropdown-light w-[200px]"
            filterOption={filterBothValueAndKey}
            popupClassName="dropdown-options-dark"
            size="small"
            title={false}
            value={params[FILTER_TYPE.INSTITUTION_ID] || []}
            onChange={(value) => {
              handleOnChangeFilter(params, FILTER_TYPE.INSTITUTION_ID, value, true);
            }}
          />
        ),
      },
      {
        text: t('Study Description'),
        key: FILTER_TYPE.STUDY_DESC,
        component: InputItem(params, FILTER_TYPE.STUDY_DESC, t('Enter Study Description')),
      },
      {
        text: t('Visit ID'),
        key: FILTER_TYPE.VISIT_ID,
        component: InputItem(params, FILTER_TYPE.VISIT_ID, t('Enter Visit ID')),
      },
      {
        text: t('Order Group'),
        key: FILTER_TYPE.ACCESSION_NO,
        component: InputItem(params, FILTER_TYPE.ACCESSION_NO, t('Enter Order Group')),
      },
      {
        text: t('Match Status'),
        key: FILTER_TYPE.MATCH_STATUS,
        component: (
          <Select
            allowClear
            className="select-dropdown-light w-[200px]"
            placeholder={t('Select Match Status')}
            popupClassName="dropdown-options-dark"
            size="small"
            suffixIcon={<CaretDownOutlined />}
            value={params[FILTER_TYPE.MATCH_STATUS]}
            onChange={(value) => {
              handleOnChangeFilter(params, FILTER_TYPE.MATCH_STATUS, value, true);
            }}
          >
            <Option value={MATCH_STATUS.Matched}>{t('Matched')}</Option>
            <Option value={MATCH_STATUS.Unmatched}>{t('Unmatched')}</Option>
          </Select>
        ),
      },
      {
        text: t('Study UID'),
        key: FILTER_TYPE.STUDY_ID,
        component: InputItem(params, FILTER_TYPE.STUDY_ID, t('Enter Study UID')),
      },
      {
        text: t('Order Date'),
        key: FILTER_TYPE.ORDER_DATE,
        component: (
          <DateRangePicker
            allowClear
            disabledDate={() => false}
            size="small"
            value={[
              dayjs(params.order_date_from || new Date()),
              dayjs(params.order_date_to || new Date()),
            ]}
            onChange={(values: dayjs.Dayjs[]) => {
              handleOnChangeFilter(
                params,
                FILTER_TYPE.ORDER_DATE,
                [values[0].startOf('days'), values[1].endOf('days')],
                true
              );
            }}
          />
        ),
      },
      {
        text: t('Esign Status'),
        hidden: false,
        key: FILTER_TYPE.ESIGN_STATUS,
        component: (
          <Filter.SelectFilter
            placeholder={t('Select Esign Status')}
            title={false}
            value={params.esign_status}
            options={enumKeys(Omit(ESIGN_STATUS, ['New'])).map((val) => ({
              label: (t(val) as string).toLocaleUpperCase(),
              value: ESIGN_STATUS[val],
            }))}
            onChange={(value) => {
              handleOnChangeFilter(params, FILTER_TYPE.ESIGN_STATUS, value, true);
            }}
          />
        ),
      },
      {
        text: t('Report Indicator'),
        hidden: false,
        key: FILTER_TYPE.LATEST_INDICATOR,
        component: (
          <Filter.SelectFilter
            placeholder={t('Select Report Indicator')}
            title={false}
            value={params.latest_indicator}
            options={enumKeys(INDICATOR).map((val) => ({
              label: (t(val) as string).toLocaleUpperCase(),
              value: INDICATOR[val],
            }))}
            onChange={(value) => {
              handleOnChangeFilter(params, FILTER_TYPE.LATEST_INDICATOR, value, true);
            }}
          />
        ),
      },
      {
        text: t('Assign To'),
        hidden: false,
        key: FILTER_TYPE.ASSIGN_TO,
        component: (
          <Filter.Creator
            mode={null}
            placeholder={t('Select Assign To')}
            title={false}
            value={params?.assign_to}
            onChange={(value) => {
              handleOnChangeFilter(params, FILTER_TYPE.ASSIGN_TO, value, true);
            }}
          />
        ),
      },
    ],
    [t, params, handleOnChangeFilter]
  );
};

export default useSearchOptions;
